/**
 * @ngdoc type
 * @name NavigationManager
 * @module mx.sb.Navigation
 *
 * @property {string} avatar - Read-only. Gets user profile avatar's url obtained from profile service.
 * @property {string} firstName - Read-only. Gets user first name obtained from profile service.
 * @property {string} lastName - Read-only. Gets user last name obtained from profile service.
 *
 * @description
 * A factory that produces Navigation Manager object that is used to manage the visibility of a side panel.
 */
import { IPromise, ITimeoutService } from 'angular';
import type { UserProfileService } from '../App/services/user-profile-service';
import type { EventService } from '../App/services/event-service';
import { EventCodes } from '../App/model/event-codes';
import type { UserProfile } from '../App/model/user-profile';
import type { NavigationItem } from './model/navigation-item';
import type { PersonalizationService } from '../App/services/personalization-service';

export class NavigationManager {
	public static readonly $inject = ['mx.shell.UserProfileService', 'mx.shell.EventService', 'mx.SolutionBuilderAgent.PersonalizationService', '$timeout'];
	public collapsedMode = true;
	public pinnedMode = false;
	public hoverMode = false;
	public isVisible = true;

	public avatar: UserProfile['avatar'] | null = null;
	public firstName: UserProfile['firstName'] | null = null;
	public lastName: UserProfile['lastName'] | null = null;
	public email: UserProfile['email'] | null = null;

	public __mouseOnNav = false;
	private closingNavTimer: IPromise<void> | undefined;

	constructor(
		private readonly userProfileService: UserProfileService,
		private readonly eventService: EventService,
		private readonly personalizationService: ReturnType<typeof PersonalizationService>,
		private readonly $timeout: ITimeoutService
	) {
		userProfileService.get().then((profile) => {
			this.setUserInfo(profile);

			this.eventService.subscribeGlobal(EventCodes.ProfileChanged, (event, profile: UserProfile) => this.setUserInfo(profile));
		});

		this.initPersonalization();
	}

	async initPersonalization() {
		const [hoverMode, pinnedMode, collapsed] = await Promise.all([
			this.personalizationService.getNavigationHoverMode(),
			this.personalizationService.getNavigationPinned(),
			this.personalizationService.getNavigationCollapsed(),
		]);

		if (typeof hoverMode === 'boolean') {
			this.hoverMode = hoverMode;
		}

		if (typeof pinnedMode === 'boolean') {
			this.pinnedMode = pinnedMode;

			if (this.pinnedMode) {
				this.collapsedMode = typeof collapsed === 'boolean' ? collapsed : false;
			}
		}
	}

	/**
	 Be very careful - it's very performance-expensive method
	 */
	public filterNavTree(item: NavigationItem) {
		return item.matchesFilterCriteria !== false;
	}

	private setUserInfo(profile: UserProfile): void {
		this.avatar = profile ? profile.avatar : null;
		this.firstName = profile ? profile.firstName : null;
		this.lastName = profile ? profile.lastName : null;
		this.email = profile ? profile.email : null;
	}

	public togglePinMode() {
		this.pinnedMode = !this.pinnedMode;
		if (this.pinnedMode && this.hoverMode) {
			this.toggleHoverMode();
		}
		this.personalizationService.setNavigationPinned(this.pinnedMode);
		this.eventService.dispatchEvent(EventCodes.LayoutChanged);
	}

	public toggleHoverMode() {
		this.hoverMode = !this.hoverMode;
		this.personalizationService.setNavigationHoverMode(this.hoverMode);
	}

	setCollapseMode(value: boolean, force = false, skipPersist = false) {
		const sendEvent = () => this.collapsedMode !== value && this.eventService.dispatchEvent(EventCodes.NavigationCollapsed, { collapsed: value });

		if (!this.pinnedMode || force) {
			if (!force && !this.pinnedMode && this.hoverMode) {
				if (this.closingNavTimer) {
					this.$timeout.cancel(this.closingNavTimer);
					this.closingNavTimer = undefined;
				}
				this.closingNavTimer = this.$timeout(
					() => {
						if ((!this.__mouseOnNav && value) || (this.__mouseOnNav && !value)) {
							sendEvent();
							this.collapsedMode = value;
						} else if (this.closingNavTimer) {
							this.$timeout.cancel(this.closingNavTimer);
							this.closingNavTimer = undefined;
						}
					},
					value ? 500 : 250
				);
			} else {
				sendEvent();
				this.collapsedMode = value;
			}

			if (this.pinnedMode && !skipPersist) {
				this.personalizationService.setNavigationCollapsed(this.collapsedMode);
			}
		}
	}

	public toggleCollapseMode(force = false) {
		this.setCollapseMode(!this.collapsedMode, force);
	}
}
